import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {Link} from 'shells/link/link';
import {LegacySetupTableEmptyState} from 'shells/table/legacy-setup-table-empty-state';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {SetupItemTable} from 'shells/table/setup-item-table';
import type {Row} from 'shells/table/types';

import type {
    GetProducts_products_edges_node as Product,
    GetMarkets_markets_edges_node as Market,
    ProductCreateInput,
    ProductUpdateInput,
    ProductDeleteInput,
    ProductUndeleteInput,
    GetProductsCategories_productCategories as ProductCategory,
} from 'nutshell-graphql-types';

import {generateAdditionalColumns, mapDataToTable, notifyUser} from './helpers';
import {ProductModal} from './product-modal/product-modal';
import {ProductConfirmArchivePopover} from './products-confirm-archive-popover';
import {ProductRowActionMenu} from './products-table-action-menu';

type Props = {
    products: Product[];
    markets: Market[];
    onProductCreate: (arg1: ProductCreateInput) => Promise<any>;
    onProductUpdate: (arg1: ProductUpdateInput) => Promise<any>;
    onProductDelete: (arg1: ProductDeleteInput) => Promise<any>;
    onProductUndelete: (arg1: ProductUndeleteInput) => Promise<any>;
    isSaving: boolean;
    isViewingArchived: boolean;
    isViewingUncategorizedProducts: boolean;
    hasNextPage: boolean;
    fetchMore: (shouldPaginate?: boolean) => Promise<any>;
    selectedCategory?: ProductCategory | null | undefined;
    categoryreportLink?: string;
};

export function ProductsTable(props: Props) {
    const [isAddingProduct, setIsAddingProduct] = React.useState<boolean>(false);
    const [isEditingProduct, setIsEditingProduct] = React.useState<boolean>(false);
    const [isDeletingProduct, setIsDeletingProduct] = React.useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);

    const columns = generateAdditionalColumns(props.markets);

    const productRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const handleProductDelete = (product: Product) => {
        setIsDeletingProduct(true);
        setSelectedProduct(product);
    };

    const handleProductUndelete = (product: Product) => {
        setSelectedProduct(product);
        props
            .onProductUndelete({productId: product.id})
            .then(() => notifyUser(product, false))
            // @ts-expect-error
            .then(setSelectedProduct(null));
    };

    const handleProductEdit = (product: Product) => {
        setIsEditingProduct(true);
        setSelectedProduct(product);
    };

    // Our initial query fetches 1000 products, we need to fetch more if not all
    // the products are loaded
    React.useEffect(() => {
        if (props.hasNextPage) {
            props.fetchMore(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.hasNextPage, props.products.length]);

    const renderRowActionMenu = (row: Row<any>, rowElement?: HTMLDivElement | null) => {
        const product: Product = row.original;

        return (
            <ProductRowActionMenu
                isForArchivedProducts={props.isViewingArchived}
                onEditProduct={() => handleProductEdit(product)}
                onDeleteProduct={() => {
                    if (rowElement) {
                        productRef.current = rowElement;
                    }
                    handleProductDelete(product);
                }}
                onUndeleteProduct={() => {
                    if (rowElement) {
                        productRef.current = rowElement;
                    }
                    handleProductUndelete(product);
                }}
            />
        );
    };

    const handleModalClose = () => {
        setIsAddingProduct(false);
        setIsEditingProduct(false);
        setIsDeletingProduct(false);
        setSelectedProduct(null);

        // Clear refs
        productRef.current = null;
    };

    const getProductsEmptyStateText = (isViewingArchived: boolean, categoryName?: string) => {
        if (isViewingArchived) {
            return <div>No archived products</div>;
        }

        if (categoryName) {
            return (
                <div>
                    No products in <span className='font-bold'>{categoryName}</span>
                </div>
            );
        }

        return <div>You haven’t created any products</div>;
    };

    const getTitle = (
        selectedCategory: ProductCategory | null | undefined,
        isViewingUncategorizedProducts: boolean,
        isViewingArchived: boolean
    ) => {
        if (isViewingArchived) {
            return 'Archived products';
        }

        if (isViewingUncategorizedProducts) {
            return 'Uncategorized products';
        }

        if (selectedCategory) {
            return `${selectedCategory.name} products`;
        }

        return 'Products';
    };

    return (
        <>
            <LegacySetupTableWrapper
                title={getTitle(
                    props.selectedCategory,
                    props.isViewingUncategorizedProducts,
                    props.isViewingArchived
                )}
                actionItem={
                    props.categoryreportLink && (
                        <div className='px-16'>
                            <Link
                                variant='secondary'
                                // @ts-expect-error
                                as={RoutedLink}
                                icon={ReportsIcon}
                                to={props.categoryreportLink}
                                newTab={true}
                            >
                                Report
                            </Link>
                        </div>
                    )
                }
            >
                <SetupItemTable
                    items={mapDataToTable(props.products)}
                    title='Product'
                    // @ts-expect-error
                    onAddRowClick={
                        props.isViewingArchived
                            ? undefined
                            : () => {
                                  setIsAddingProduct(true);
                              }
                    }
                    onSelectRow={
                        props.isViewingArchived
                            ? undefined
                            : (row) => handleProductEdit(row.original)
                    }
                    error={null}
                    sortBy={[{id: 'product', desc: false}]}
                    isLoading={false}
                    emptyState={
                        <LegacySetupTableEmptyState
                            text={getProductsEmptyStateText(
                                props.isViewingArchived,
                                props.selectedCategory ? props.selectedCategory.name : undefined
                            )}
                        />
                    }
                    renderRowActionMenu={renderRowActionMenu}
                    getRowRef={(ref) => {
                        productRef.current = ref || null;
                    }}
                    getAddButtonRef={(ref) => {
                        productRef.current = ref || null;
                    }}
                    additionalColumns={columns}
                    primaryColSubtitleField='categoryName'
                    primaryColTertiaryField='description'
                    includeCreatedTimeCol={false}
                />
            </LegacySetupTableWrapper>
            {isAddingProduct && (
                <ProductModal
                    variant='create'
                    markets={props.markets}
                    onClose={handleModalClose}
                    isOpen={true}
                    onProductSave={props.onProductCreate}
                    isSaving={props.isSaving}
                    defaultCategory={props.selectedCategory}
                />
            )}
            {isEditingProduct && selectedProduct && (
                <ProductModal
                    variant='update'
                    product={selectedProduct}
                    onClose={handleModalClose}
                    isOpen={true}
                    onProductSave={props.onProductUpdate}
                    isSaving={props.isSaving}
                />
            )}
            {isDeletingProduct && selectedProduct && (
                <ProductConfirmArchivePopover
                    anchor={productRef.current}
                    onClose={handleModalClose}
                    onSubmit={props.onProductDelete}
                    product={selectedProduct}
                />
            )}
        </>
    );
}
